import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Signature } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { User } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';
import { AssetReturnForm } from '../../components/AssetReturnForm';

const ReturnAsset: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const targetStatus = location.state?.targetStatus || 'Available';

    const [asset, setAsset] = useState<Asset | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetRes, usersRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<{ users: User[] }>('/api/users?all_users=true')
                ]);
                setAsset(assetRes);
                setUsers(usersRes.users || []);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load required data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT)) {
        return <AccessRestricted />;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!asset) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">Asset not found</h2>
                <Button onClick={() => navigate('/assets')} className="mt-4">Back to Dashboard</Button>
            </div>
        );
    }

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        Return Asset
                    </h1>
                    <p className="text-gray-500 text-sm font-medium mt-0.5">
                        Complete the form to hand over the asset
                    </p>
                </div>
            </div>

            {/* Banner */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 mb-8 flex items-start gap-4 shadow-sm">
                <div className="p-2 bg-blue-100 rounded-xl shrink-0 mt-0.5">
                    <Check size={18} className="text-blue-600" />
                </div>
                <div>
                    <h3 className="font-bold text-blue-900 text-sm mb-1">Status Update Details</h3>
                    <p className="text-blue-800 text-sm font-medium leading-relaxed">
                        You are seeing this page because you are making this asset <strong>{targetStatus}</strong> again.
                        Please complete the form below and submit to confirm the asset is ready to be marked as {targetStatus}.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form */}
                <div className="lg:col-span-2">
                    <AssetReturnForm
                        asset={asset}
                        receivers={users}
                        targetStatus={targetStatus}
                        onCancel={() => navigate(-1)}
                        onReturned={(assetId) => navigate(`/assets/${assetId}`)}
                    />
                </div>

                {/* Info Sidebar */}
                <div className="hidden lg:block space-y-6">
                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                                <Signature size={20} />
                            </div>
                            <h3 className="font-bold text-gray-900 text-sm">Return Policy</h3>
                        </div>
                        <div className="space-y-4 text-sm font-medium text-gray-600">
                            <p>
                                All asset returns must be documented properly to maintain accurate inventory records and audit trails.
                            </p>
                            <ul className="space-y-3 pt-2 border-t border-gray-200/60">
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                    <span>If the asset is damaged, attach photos to this record.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                    <span>The receiving person's signature is strictly required to complete the handover chain of custody.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReturnAsset;
